import {action, makeObservable, observable} from 'mobx';
import LineInterface from 'app/interfaces/LineInterface';
import MyBetInterface from 'app/interfaces/MyBetInterface';
import BuyoutInterface from 'app/interfaces/BuyoutInterface';
import {BuyoutRatesInterface, calculateBuyoutAmount} from 'app/utils/buyout';
import {GAMER_1, GAMER_2} from 'app/utils/Consts';

export interface LineQuoteInterface {
    coef_1: number;
    coef_2: number;
    winner: number;
    buyout_disabled: boolean;
}

const lineQuoteFromBet = (bet: MyBetInterface): LineQuoteInterface => ({
    buyout_disabled: Boolean(bet.bet.buyout_disabled),
    coef_1: Number(bet.bet.coef_1),
    coef_2: Number(bet.bet.coef_2),
    winner: bet.bet.winner
});

export class BuyoutStore {
    @observable quotes = new Map<number, LineQuoteInterface>();

    @observable pending = new Map<string, BuyoutInterface>();

    @observable quoting = new Set<string>();

    watchedLines = new Set<number>();

    constructor() {
        makeObservable(this);
    }

    watchBets = (bets: MyBetInterface[]): void => {
        this.watchedLines = new Set(bets.filter(bet => bet.bet).map(bet => Number(bet.bet.id)));
    };

    @action
        applyLineUpdate = (payload: LineInterface): void => {
            if (!this.watchedLines.has(Number(payload.id))) {
                return;
            }

            this.quotes.set(payload.id, {
                buyout_disabled: Boolean(payload.buyout_disabled),
                coef_1: Number(payload.coef_1),
                coef_2: Number(payload.coef_2),
                winner: payload.winner
            });
        };

    @action
        applyQuotedCoef = (bet: MyBetInterface, coef: number): void => {
            const quote = this.lineQuote(bet);
            this.quotes.set(bet.bet.id, bet.on === GAMER_1 ? {...quote, coef_1: coef} : {...quote, coef_2: coef});
        };

    @action
        setQuoting = (betId: string | number, value: boolean): void => {
            if (value) {
                this.quoting.add(String(betId));
            } else {
                this.quoting.delete(String(betId));
            }
        };

    isQuoting = (betId: string | number): boolean => this.quoting.has(String(betId));

    @action
        setPending = (betId: string | number, buyout: BuyoutInterface): void => {
            this.pending.set(String(betId), buyout);
        };

    @action
        clearPending = (betId: string | number): void => {
            this.pending.delete(String(betId));
        };

    @action
        reset = (): void => {
            this.quotes.clear();
            this.pending.clear();
            this.quoting.clear();
            this.watchedLines.clear();
        };

    lineQuote = (bet: MyBetInterface): LineQuoteInterface => this.quotes.get(bet.bet.id) || lineQuoteFromBet(bet);

    pendingFor = (bet: MyBetInterface): BuyoutInterface | null => this.pending.get(String(bet.id)) || bet.buyout || null;

    lineAcceptsBets = (bet: MyBetInterface): boolean => {
        const quote = this.lineQuote(bet);
        return quote.winner === 0 && !quote.buyout_disabled && this.currentCoef(bet) > 0;
    };

    currentCoef = (bet: MyBetInterface): number => {
        const quote = this.lineQuote(bet);

        if (bet.on === GAMER_1) {
            return Number(quote.coef_1) || 0;
        }
        return bet.on === GAMER_2 ? Number(quote.coef_2) || 0 : 0;
    };

    amountFor = (bet: MyBetInterface, rates: BuyoutRatesInterface): number | null => {
        if (!rates.enabled || !this.lineAcceptsBets(bet)) {
            return null;
        }

        return calculateBuyoutAmount({
            amount: bet.amount,
            betCoef: bet.coef,
            currentCoef: this.currentCoef(bet),
            rates
        });
    };
}

export const buyoutStore = new BuyoutStore();
